import { RovoExtAdminSettings } from "../gateway-client/gatewayClient";
import type { UserOptions } from "../../components/options/optionsTypes";
import { LocalStorageKey } from "../localStorage";

/**
 * Remote admin settings with defaults applied for the admin-controlled fields,
 * ensuring consumers don't have to handle `undefined` for them.
 *
 * `controlledUserNtpRedirectUrl` is included so consumers always see a string
 * (`""` meaning "no admin override") rather than having to `?? ""` at every
 * read site.
 */
export type DefaultedRemoteAdminSettings = RovoExtAdminSettings &
  Required<
    Pick<
      RovoExtAdminSettings,
      | "controlledUserNtpEnabled"
      | "controlledUserRovoCompanionEnabled"
      | "controlledUserGdocsDefinitionsEnabled"
      | "controlledUserDomContentEnabled"
      | "controlledUserCompanyAnnouncements"
      | "controlledUserNtpRedirectUrl"
    >
  >;

export enum RemoteAdminSettingsErrorType {
  QUERY_ERROR = "QUERY_ERROR",
  MUTATION_ERROR = "MUTATION_ERROR",
}

export class RemoteAdminSettingsError extends Error {
  readonly type: RemoteAdminSettingsErrorType;
  readonly originalError: Error;
  readonly timestamp: number;
  readonly context?: {
    settingKey?: keyof RovoExtAdminSettings;
    attemptedValue?: unknown;
    previousValue?: unknown;
  };

  constructor({
    type,
    originalError,
    context,
  }: {
    type: RemoteAdminSettingsErrorType;
    originalError: Error;
    context?: RemoteAdminSettingsError["context"];
  }) {
    super(originalError.message);
    this.name = "RemoteAdminSettingsError";
    this.type = type;
    this.originalError = originalError;
    this.timestamp = Date.now();
    this.context = context;
  }
}

export interface UpdateRemoteSettingParams<K extends keyof RovoExtAdminSettings> {
  key: K;
  value: RovoExtAdminSettings[K];
}

export type LocalStoredSettingsKey =
  | LocalStorageKey.WELCOME_HEADER_ENABLED
  | LocalStorageKey.GLOBAL_CREATE_ENABLED
  | LocalStorageKey.LOCAL_DEV_BANNER_ENABLED
  | LocalStorageKey.CALENDAR_ENABLED
  | LocalStorageKey.RECENT_WORK_ENABLED
  | LocalStorageKey.BOOKMARKS_ENABLED
  | LocalStorageKey.ANNOUNCEMENTS_ENABLED;

export type LocalStoredSettings = Record<LocalStoredSettingsKey, boolean>;

export interface UpdateLocalStoredSettingParams<K extends LocalStoredSettingsKey> {
  key: K;
  value: LocalStoredSettings[K];
}

export type ChromeStoredSettings = UserOptions;
export type ChromeStoredSettingsKey = keyof ChromeStoredSettings;

export interface UpdateChromeStoredSettingParams<K extends ChromeStoredSettingsKey> {
  key: K;
  value: ChromeStoredSettings[K];
}

export interface SettingsContextValue {
  // Remote (admin controlled) settings
  remoteAdminSettings: DefaultedRemoteAdminSettings;
  updateRemoteAdminSettings: <K extends keyof RovoExtAdminSettings>(
    params: UpdateRemoteSettingParams<K>,
  ) => Promise<void>;
  refreshRemoteAdminSettings: () => Promise<void>;
  isLoadingRemoteAdminSettings: boolean;
  remoteAdminSettingsQueryError: RemoteAdminSettingsError | null;
  remoteAdminSettingsMutationError: RemoteAdminSettingsError | null;

  // Local (user) settings
  localStoredSettings: LocalStoredSettings;
  updateLocalStoredSetting: <K extends LocalStoredSettingsKey>(params: UpdateLocalStoredSettingParams<K>) => void;

  // Chrome stored settings (chrome.storage-backed)
  chromeStoredSettings: ChromeStoredSettings;
  updateChromeStoredSetting: <K extends ChromeStoredSettingsKey>(params: UpdateChromeStoredSettingParams<K>) => void;
}

export type { RovoExtAdminSettings };
